import { useEffect, useState } from 'react';
import { useAudioRecorder } from 'react-audio-voice-recorder';
import { useDispatch } from 'react-redux';

import { AudioRecorderDefaultConfig } from '../../components/audioRecorder/AudioRecorder';
import { changeSelectedMic } from '../../redux/appConfigSlice';
import { Dropdown } from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/DropdownItem';
import { MicObject } from '../../interface/interface';
import { isHeadsetMicrophone } from '../../service/MicService';

interface MicrophoneSelectionScreenProps {}

/* Display the microphone selection screen with audio visualizer and microphone options */
const MicrophoneSelectionScreen = (props: MicrophoneSelectionScreenProps) => {
    const [micList, setMicList] = useState([]);
    const [selectedMicId, setSelectedMicId] = useState<MicObject>({ deviceId: '', label: '' });
    const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);

    let recorderConfig = { ...AudioRecorderDefaultConfig };
    let audioRecorder = useAudioRecorder({
        ...recorderConfig,
    });
    const dispatch = useDispatch();

    useEffect(() => {
        getMicrophoneListAsync();
        window.navigator.mediaDevices.addEventListener('devicechange', () => {
            getMicrophoneListAsync();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* Get the list of available microphones and start recording with default one */
    const getMicrophoneListAsync = async () => {
        const allDevices = await window.navigator.mediaDevices.enumerateDevices();
        const micList = allDevices.filter((d) => d.kind === 'audioinput');
        let filteredMicList: any = [];
        console.log('Microphone list updated:', micList);
        micList.forEach((mic) => {
            if (/^[0-9a-f]+$/i.test(mic.deviceId)) {
                filteredMicList.push({
                    label: mic.label,
                    deviceId: mic.deviceId,
                    groupId: mic.groupId,
                });
            }
        });
        setMicList(filteredMicList);
        setSelectedMicId({
            deviceId: filteredMicList[0]?.deviceId,
            label: filteredMicList[0]?.label,
        });
        if (filteredMicList.length !== 0) {
            dispatch(
                changeSelectedMic({
                    deviceId: filteredMicList[0].deviceId,
                    label: filteredMicList[0].label,
                })
            );
        }
        startRecord();
    };

    /* Start recording with the selected microphone */
    const startRecord = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            setMediaStream(stream);
            audioRecorder.startRecording();
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };

    /* Stop recording and stop the media stream */
    const stopRecording = async (nextBtn = false) => {
        audioRecorder.stopRecording();
        if (mediaStream) {
            mediaStream.getTracks().forEach((track) => track.stop());
            setMediaStream(null);
        }
        if (nextBtn) {
            dispatch(changeSelectedMic(selectedMicId));
        }
    };

    const onSelectDropdown = (value: string) => {
        stopRecording(true);

        const selected = micList.find((mic: MicObject) => mic.deviceId === value);
        if (!selected) {
            return;
        }
        setSelectedMicId(selected);
        dispatch(changeSelectedMic(selected));
    };

    return (
        <Dropdown id="microphone" onSelect={onSelectDropdown} value={selectedMicId.deviceId} label="Choose your microphone">
            {micList.map((mic: any) => {
                return <DropdownItem label={mic.label} key={mic.deviceId} value={mic.deviceId} />;
            })}
        </Dropdown>
    );
};

export default MicrophoneSelectionScreen;
